// @ts-strict-ignore
import { useCallback, useContext, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { RRule } from 'rrule';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

interface Props {
  maxCount: number;
  rruleString: string | undefined;
  onChange(rule: RRule | undefined): void;
}

export function MBRecurrencePickerSelectCount({ rruleString, maxCount, onChange }: Props) {
  const colors = useContext(ColorThemeContext);
  const rule = useMemo(
    () => (!!rruleString ? RRule.fromString(rruleString) : undefined),
    [rruleString],
  );

  const [value, setValue] = useState(rule?.options.count?.toString() ?? '');
  const [showError, setShowError] = useState(false);

  const handleChange = useCallback(
    (updatedValue: string) => {
      const cleanedValue = updatedValue.replace(/\D/g, '');
      setValue(cleanedValue);

      if (!cleanedValue) {
        return;
      }
      const count = Number(cleanedValue);
      if (count > maxCount) {
        setShowError(true);
      } else {
        setShowError(false);
        onChange(new RRule({ ...rule.options, until: undefined, count }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rule, value, onChange],
  );

  return (
    <View padding={2}>
      <View row centerVertically>
        <MBIconV2
          name="repeat"
          size={grid(4)}
          color={showError ? colors.error.foreground : colors.accent}
        />
        <MBTextInput
          noBorder
          selectTextOnFocus
          style={[
            styles.input,
            { backgroundColor: showError ? colors.error.background : colors.backgroundInactive },
          ]}
          keyboardType="number-pad"
          autoFocus
          value={value}
          onChangeText={handleChange}
        />
        <MBTextBody1 color={colors.foregroundActive}>
          {i18n('card.blocks.recurrenceInput.ends.afterXTimes.times')}
        </MBTextBody1>
      </View>
      {showError && (
        <MBTextBody2 color={colors.error.foreground}>
          {i18n('card.blocks.recurrenceInput.ends.afterXTimes.error', { maxCount })}
        </MBTextBody2>
      )}
    </View>
  );
}

export const styles = StyleSheet.create({
  input: {
    paddingVertical: grid(1),
    paddingHorizontal: grid(2),
    borderRadius: grid(1),
    width: grid(20),
    marginHorizontal: grid(1),
  },
});
